<template>
  <div class="text-left" :class="{ 'is-invalid': $v.inputValue.$error }">
    <label :for="baseId" v-html="addNbsp(getLocaleText(inputData.label))" />

    <v-row :id="baseId">
      <v-col cols="12">
        <v-text-field
          :id="`${baseId}-street`"
          ref="streetField"
          v-model="street"
          solo
          flat
          :placeholder="$t('addressInput.street')"
          :class="{
            'is-valid':
              $v.inputValue.$dirty &&
              !$v.inputValue.$invalid &&
              ((!inputData.required && inputValue?.length) || inputData.required)
          }"
          :error="$v.inputValue.$error"
          hide-details="auto"
          @focus="handleFocus"
          @keydown.enter.prevent.stop=""
        >
          <template #append>
            <v-icon
              v-if="
                $v.inputValue.$dirty &&
                !$v.inputValue.$invalid &&
                ((!inputData.required && inputValue?.length) || inputData.required)
              "
            >
              $tick
            </v-icon>
            <v-icon v-if="$v.inputValue.$error"> $warning </v-icon>
          </template>
        </v-text-field>
      </v-col>

      <v-col cols="12" md="6">
        <v-text-field
          :id="`${baseId}-postcode`"
          ref="postcodeField"
          v-model="postcode"
          solo
          flat
          :placeholder="$t('addressInput.postcode')"
          :class="{
            'is-valid':
              $v.inputValue.$dirty &&
              !$v.inputValue.$invalid &&
              ((!inputData.required && inputValue?.length) || inputData.required)
          }"
          :error="$v.inputValue.$error"
          hide-details="auto"
          @focus="handleFocus"
          @keydown.enter.prevent.stop=""
        >
          <template #append>
            <v-icon
              v-if="
                $v.inputValue.$dirty &&
                !$v.inputValue.$invalid &&
                ((!inputData.required && inputValue?.length) || inputData.required)
              "
            >
              $tick
            </v-icon>
            <v-icon v-if="$v.inputValue.$error"> $warning </v-icon>
          </template>
        </v-text-field>
      </v-col>

      <v-col cols="12" md="6">
        <v-text-field
          :id="`${baseId}-country`"
          ref="countryField"
          v-model="country"
          solo
          flat
          :placeholder="$t('addressInput.country')"
          :class="{
            'is-valid':
              $v.inputValue.$dirty &&
              !$v.inputValue.$invalid &&
              ((!inputData.required && inputValue?.length) || inputData.required)
          }"
          :error="$v.inputValue.$error"
          hide-details="auto"
          @focus="handleFocus"
          @keydown.enter.prevent.stop=""
        >
          <template #append>
            <v-icon
              v-if="
                $v.inputValue.$dirty &&
                !$v.inputValue.$invalid &&
                ((!inputData.required && inputValue?.length) || inputData.required)
              "
            >
              $tick
            </v-icon>
            <v-icon v-if="$v.inputValue.$error"> $warning </v-icon>
          </template>
        </v-text-field>
      </v-col>
    </v-row>

    <div v-if="!!helpText && !!helpText.length" class="v-text-field__details">
      <div class="v-messages theme--light" role="alert">
        <div class="v-messages__wrapper">
          <div :inner-html.prop="helpText" class="v-messages__message text-body-small" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import formGtm from '@/mixins/formGtm';
import { geocodeAddress, parseLooseAddress } from '@/utils/geocode.js';

export default {
  name: 'PlainAddress',
  mixins: [formGtm],
  props: {
    inputData: { type: Object, required: true },
    value: { type: String, default: '' },
    errorText: { type: String, default: '' }
  },
  data() {
    return {
      inputValue: this.value || '',
      street: '',
      postcode: '',
      country: '',
      hydrated: false,
      userEdited: false,
      structuredMeta: {
        formatted: '',
        country: '',
        countryCode: '',
        zip: '',
        city: '',
        streetName: '',
        streetNumber: '',
        lat: null,
        lon: null,
        placeId: null
      },
      geocodeRequestId: 0,
      geocodeTimeout: null,
      autocomplete: {
        street: null,
        postcode: null,
        country: null
      }
    };
  },
  validations() {
    if (this.inputData.required) {
      return { inputValue: { required } };
    }
    return { inputValue: {} };
  },
  computed: {
    baseId() {
      return `dynamicInput-${this.inputData.id}`;
    },
    helpText() {
      return this.addNbsp(this.getLocaleText(this.inputData.help));
    }
  },
  watch: {
    value(val) {
      if (val === this.inputValue) {
        return;
      }
      const incomingValue = val || '';
      const shouldReset = !incomingValue;
      this.hydrated = false;
      this.inputValue = incomingValue;
      if (shouldReset) {
        this.street = '';
        this.postcode = '';
        this.country = '';
        this.userEdited = false;
        this.emitAll();
      } else {
        this.prefillFromValueOrMeta();
      }
      this.$nextTick(() => {
        this.hydrated = true;
      });
    },
    street() {
      if (!this.hydrated) {
        return;
      }
      this.userEdited = true;
      this.$nextTick(this.updateAddressText);
    },
    postcode() {
      if (!this.hydrated) {
        return;
      }
      this.userEdited = true;
      this.$nextTick(this.updateAddressText);
    },
    country() {
      if (!this.hydrated) {
        return;
      }
      this.userEdited = true;
      this.$nextTick(this.updateAddressText);
    }
  },
  created() {
    this.prefillFromValueOrMeta();
    this.hydrated = true;
    this.$v.$touch();
    if (this.inputValue) {
      this.emitAll(true);
    }
  },
  mounted() {
    this.initAutocomplete();
  },
  methods: {
    handleFocus() {},
    getAutocompleteInput(refName) {
      const ref = this.$refs[refName];
      if (!ref) {
        return null;
      }
      return ref.$refs && ref.$refs.input ? ref.$refs.input : ref.$el?.querySelector('input');
    },
    initAutocomplete(retries = 5) {
      const ready =
        typeof window !== 'undefined' &&
        window.google &&
        google.maps &&
        google.maps.places &&
        typeof google.maps.places.Autocomplete === 'function';

      if (!ready) {
        if (retries > 0) {
          setTimeout(() => this.initAutocomplete(retries - 1), 250);
        }
        return;
      }

      const streetInput = this.getAutocompleteInput('streetField');
      const postcodeInput = this.getAutocompleteInput('postcodeField');
      const countryInput = this.getAutocompleteInput('countryField');

      if (streetInput) {
        this.autocomplete.street = new google.maps.places.Autocomplete(streetInput, {
          fields: ['formatted_address', 'address_components', 'geometry', 'place_id', 'name'],
          types: ['address']
        });
        this.autocomplete.street.addListener('place_changed', () => {
          const place = this.autocomplete.street.getPlace();
          this.handlePlaceSelection(place, 'street');
        });
      }

      if (postcodeInput) {
        this.autocomplete.postcode = new google.maps.places.Autocomplete(postcodeInput, {
          fields: ['formatted_address', 'address_components', 'place_id', 'name'],
          types: ['postal_code']
        });
        this.autocomplete.postcode.addListener('place_changed', () => {
          const place = this.autocomplete.postcode.getPlace();
          this.handlePlaceSelection(place, 'postcode');
        });
      }

      if (countryInput) {
        this.autocomplete.country = new google.maps.places.Autocomplete(countryInput, {
          fields: ['address_components', 'name'],
          types: ['country']
        });
        this.autocomplete.country.addListener('place_changed', () => {
          const place = this.autocomplete.country.getPlace();
          this.handlePlaceSelection(place, 'country');
        });
      }
    },
    handlePlaceSelection(place, source) {
      if (!place || !place.address_components) {
        return;
      }
      const meta = this.buildMetaFromPlace(place);
      // Always update meta from the selected place (safe merge)
      this.applyIncomingMeta({ ...this.structuredMeta, ...meta });
      this.hydrated = false;
      if (source === 'street') {
        this.street = meta.streetLine || place.name || '';
        this.postcode = meta.zip || '';
        this.country = meta.country || '';
      } else if (source === 'postcode') {
        this.street = meta.streetLine || '';
        this.postcode = meta.zip || place.name || '';
        this.country = meta.country || '';
      } else if (source === 'country') {
        this.country = meta.country || place.name || '';
        // Country autocomplete neposkytuje ulici ani PSČ — nevyplňujeme jiná pole
      }
      // Pro street source použij Google formatted_address (má správné PSČ + město).
      // Pro postcode/country formatted_address buď chybí nebo neobsahuje ulici → composeAddress().
      this.inputValue =
        source === 'street' && place.formatted_address
          ? place.formatted_address
          : this.composeAddress();
      this.userEdited = true;
      this.cancelGeocodeTimeout();
      // Restrict autocomplete suggestions based on detected/selected country
      this.restrictAutocompleteCountries(meta.countryCode);
      // hydrated se obnovuje až v $nextTick (ne synchronně) — jinak watche na street/postcode/country
      // flusují po synchronním kódu ale před $nextTick, takže by updateAddressText přepsalo inputValue
      this.$nextTick(() => {
        this.hydrated = true;
        this.emitAll(true);
      });
    },
    restrictAutocompleteCountries(countryCode) {
      if (!countryCode) {
        return;
      }
      const cc = countryCode.toLowerCase();
      if (this.autocomplete.street?.setComponentRestrictions) {
        this.autocomplete.street.setComponentRestrictions({ country: cc });
      }
      if (this.autocomplete.postcode?.setComponentRestrictions) {
        this.autocomplete.postcode.setComponentRestrictions({ country: cc });
      }
    },
    buildMetaFromPlace(place) {
      const comps = place.address_components || [];
      const get = type => comps.find(ac => Array.isArray(ac.types) && ac.types.includes(type));
      const country = get('country')?.long_name || '';
      const countryCode = get('country')?.short_name || '';
      const zip = get('postal_code')?.long_name || '';
      const city =
        get('locality')?.long_name ||
        get('postal_town')?.long_name ||
        get('administrative_area_level_2')?.long_name ||
        '';
      const streetName = get('route')?.long_name || '';
      const streetNumber = get('street_number')?.long_name || get('premise')?.long_name || '';
      // Vždy preferujeme první segment formatted_address — obsahuje kompletní adresu ulice
      // včetně českého dvojího číslování "Hartigova 1735/170" (komponenta street_number vrací
      // pouze orientační číslo "170"). Fallback na složení z komponent pro případ, že
      // formatted_address chybí nebo nezačíná ulicí.
      const streetLine =
        place.formatted_address?.split(',')[0]?.trim() ||
        (streetName ? [streetName, streetNumber].filter(Boolean).join(' ').trim() : '') ||
        place.name ||
        streetNumber ||
        '';
      const lat = place.geometry?.location?.lat ? place.geometry.location.lat() : null;
      const lon = place.geometry?.location?.lng ? place.geometry.location.lng() : null;
      const formatted = place.formatted_address || streetLine || this.composeAddress();

      return {
        formatted,
        country,
        countryCode,
        zip,
        city,
        streetName,
        streetNumber,
        streetLine,
        lat,
        lon,
        placeId: place.place_id || null
      };
    },
    prefillFromValueOrMeta() {
      const meta = this.inputData.meta || {};
      const parsedMeta = meta.formatted ? parseLooseAddress(meta.formatted) : {};
      const mergedMeta = { ...parsedMeta, ...meta };
      const hasStructured =
        mergedMeta.streetName || mergedMeta.zip || mergedMeta.country || mergedMeta.city;
      // Priority pro zobrazovanou hodnotu ulice:
      // 1. streetLine — explicitně uložený display string (vesnice, výsledek autocomplete)
      // 2. parsedMeta.city — první segment formatted_address, obsahuje kompletní české číslování
      //    (např. "Biskupská 1154/10"), zatímco streetName+streetNumber z DB mají jen "Biskupská"+"10"
      // 3. streetName + streetNumber — fallback když formatted není k dispozici
      const fullStreet =
        mergedMeta.streetLine ||
        parsedMeta.city ||
        `${mergedMeta.streetName || ''} ${mergedMeta.streetNumber || ''}`.trim();
      if (hasStructured || mergedMeta.formatted) {
        this.street = fullStreet || mergedMeta.streetName || mergedMeta.city || '';
        this.postcode = mergedMeta.zip || '';
        this.country = mergedMeta.country || '';
        this.inputValue = mergedMeta.formatted || this.composeAddress();
        this.userEdited = false;
        this.applyIncomingMeta(mergedMeta);
        this.restrictAutocompleteCountries(mergedMeta.countryCode);
        return;
      }
      if (this.inputValue) {
        const parsed = parseLooseAddress(this.inputValue);
        this.street = parsed.city || '';
        this.postcode = parsed.zip || '';
        this.country = parsed.country || '';
        this.userEdited = false;
        this.applyIncomingMeta({
          formatted: this.inputValue,
          country: this.country,
          zip: this.postcode,
          streetName: this.street
        });
      } else {
        this.resetStructuredMeta();
      }
    },
    composeAddress() {
      const parts = [this.street?.trim(), this.postcode?.trim(), this.country?.trim()].filter(
        Boolean
      );
      return parts.join(', ');
    },
    applyIncomingMeta(meta = {}) {
      this.structuredMeta = {
        formatted: meta.formatted || this.composeAddress(),
        country: meta.country || '',
        countryCode: meta.countryCode || '',
        zip: meta.zip || meta.postcode || '',
        city: meta.city || '',
        streetName: meta.streetName || '',
        streetNumber: meta.streetNumber || '',
        lat: meta.lat ?? null,
        lon: meta.lon ?? null,
        placeId: meta.placeId || null
      };
    },
    resetStructuredMeta(overrides = {}) {
      this.structuredMeta = {
        formatted: overrides.formatted || this.composeAddress(),
        country: overrides.country || this.country || '',
        countryCode: overrides.countryCode || '',
        zip: overrides.zip || this.postcode || '',
        city: overrides.city || '',
        streetName: overrides.streetName || this.street || '',
        streetNumber: overrides.streetNumber || '',
        lat: overrides.lat ?? null,
        lon: overrides.lon ?? null,
        placeId: overrides.placeId || null
      };
    },
    scheduleGeocode() {
      this.cancelGeocodeTimeout();
      this.geocodeTimeout = setTimeout(() => {
        this.geocodeTimeout = null;
        this.performGeocode();
      }, 500);
    },
    cancelGeocodeTimeout() {
      if (this.geocodeTimeout) {
        clearTimeout(this.geocodeTimeout);
        this.geocodeTimeout = null;
      }
    },
    async performGeocode() {
      const formatted = this.composeAddress();
      if (!formatted) {
        this.resetStructuredMeta();
        this.emitAll();
        return;
      }
      const requestId = ++this.geocodeRequestId;
      try {
        const meta = await geocodeAddress(formatted);
        if (requestId !== this.geocodeRequestId) {
          return;
        }
        const safeMeta = meta && typeof meta === 'object' ? meta : parseLooseAddress(formatted);
        this.applyIncomingMeta({
          ...safeMeta,
          formatted: safeMeta.formatted || formatted,
          country: safeMeta.country || this.country || '',
          zip: safeMeta.zip || safeMeta.postcode || this.postcode || '',
          streetName: safeMeta.streetName || this.street || ''
        });
      } catch (error) {
        if (requestId !== this.geocodeRequestId) {
          return;
        }
        this.resetStructuredMeta({ formatted });
      }
      this.emitAll();
    },
    buildStructured() {
      const meta = this.structuredMeta || {};
      const formatted = this.inputValue || meta.formatted || this.composeAddress();
      // streetLine = zobrazovaná hodnota v street inputu (správná i pro vesnice bez route)
      const streetLine =
        this.street || [meta.streetName, meta.streetNumber].filter(Boolean).join(' ').trim() || '';
      return {
        formatted,
        country: meta.country || this.country || '',
        countryCode: meta.countryCode || '',
        zip: meta.zip || meta.postcode || this.postcode || '',
        city: meta.city || '',
        streetName: meta.streetName || this.street || '',
        streetNumber: meta.streetNumber || '',
        streetLine,
        lat: meta.lat ?? null,
        lon: meta.lon ?? null,
        placeId: meta.placeId || null
      };
    },
    updateAddressText() {
      if (!this.hydrated) {
        return;
      }
      const hasAll = this.street?.trim() && this.postcode?.trim() && this.country?.trim();
      if (hasAll) {
        this.inputValue = this.composeAddress();
        this.resetStructuredMeta({ formatted: this.inputValue });
        this.scheduleGeocode();
      } else if (this.userEdited) {
        this.inputValue = '';
        this.cancelGeocodeTimeout();
        this.resetStructuredMeta();
      }
      this.emitAll();
    },
    emitAll(forceValidTouch = false) {
      if (forceValidTouch) {
        this.$v.$touch();
      }
      this.$emit('input', this.inputValue || '');
      this.$emit('update:value', this.inputValue || '');
      this.$emit('valid-change', !this.$v.$invalid);
      this.$emit('address-structured', this.buildStructured());
    },
    validate() {
      this.$v.$touch();
    },
    reset() {
      this.$v.$reset();
    }
  }
};
</script>

<style scoped lang="scss">
::v-deep {
  input {
    text-align: center;
  }
}
</style>
